import { cn } from '@/lib/utils';

export type SwapFilterStatus = 'pending' | 'completed' | 'cancelled' | 'all';

interface SwapStatusFilterProps {
  selectedStatus: SwapFilterStatus;
  onStatusChange: (status: SwapFilterStatus) => void;
  className?: string;
  counts?: {
    pending: number;
    completed: number;
    cancelled: number;
    all: number;
  };
}

const FILTER_OPTIONS: Array<{
  value: SwapFilterStatus;
  label: string;
  description: string;
}> = [
  { 
    value: 'pending', 
    label: 'Pending', 
    description: 'Awaiting response' 
  },
  { 
    value: 'completed', 
    label: 'Completed', 
    description: 'Successfully swapped' 
  },
  { 
    value: 'cancelled', 
    label: 'Cancelled', 
    description: 'Cancelled or declined' 
  },
  { 
    value: 'all', 
    label: 'All', 
    description: 'All requests' 
  },
];

export function SwapStatusFilter({ 
  selectedStatus, 
  onStatusChange, 
  className,
  counts 
}: SwapStatusFilterProps) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {FILTER_OPTIONS.map((option) => {
        const isSelected = selectedStatus === option.value;
        const count = counts?.[option.value];
        
        return (
          <button
            key={option.value}
            onClick={() => onStatusChange(option.value)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
              "border border-border hover:border-primary/50",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              isSelected
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "bg-background text-muted-foreground hover:text-foreground hover:bg-accent"
            )}
            title={option.description}
          >
            <span>{option.label}</span>
            {count !== undefined && (
              <span
                className={cn(
                  "px-1.5 py-0.5 rounded-full text-xs font-semibold min-w-[20px] text-center",
                  isSelected
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Helper function to convert filter status to database status array
export function getStatusFilterArray(
  filterStatus: SwapFilterStatus, 
  context: 'requests' | 'offers' = 'requests'
): string[] | undefined {
  if (context === 'offers') {
    // For swap offers (swap_candidates table)
    switch (filterStatus) {
      case 'pending':
        return ['pending']; // ✅ FIXED: Match swap_candidates status for pending offers
      case 'completed':
        return ['accepted']; // ✅ FIXED: Match swap_candidates status for completed offers
      case 'cancelled':
        return ['declined', 'expired']; // ✅ FIXED: Match swap_candidates status for cancelled offers
      case 'all':
        return undefined; // No filter
      default:
        return undefined;
    }
  }
  
  // Default to requests (shift_swaps table) - existing logic
  switch (filterStatus) {
    case 'pending':
      return ['open', 'manager_pending']; // ✅ FIXED: Match shift_swaps status for pending requests
    case 'completed':
      return ['finalized']; // ✅ FIXED: Match shift_swaps status for completed requests
    case 'cancelled':
      return ['cancelled', 'expired']; // ✅ FIXED: Match shift_swaps status for cancelled requests
    case 'all':
      return undefined; // No filter
    default:
      return undefined;
  }
}

// Helper function to calculate counts from data
export function calculateSwapCounts<T extends { status: string; candidate_status?: string }>(
  data: T[]
): { pending: number; completed: number; cancelled: number; all: number } {
  const counts = {
    pending: 0,
    completed: 0,
    cancelled: 0,
    all: data.length,
  };

  data.forEach((item) => {
    // For swap requests, use the status field directly (shift_swaps table)
    const status = item.status;
    
    if (status === 'open' || status === 'manager_pending') { // ✅ FIXED: Match shift_swaps status for pending
      counts.pending++;
    } else if (status === 'finalized') { // ✅ FIXED: Match shift_swaps status for completed
      counts.completed++;
    } else if (status === 'cancelled' || status === 'expired') { // ✅ FIXED: Match shift_swaps status for cancelled
      counts.cancelled++;
    }
  });

  return counts;
}
